import React from 'react';
import PropTypes from 'prop-types';
import { Chip, Tooltip, Box } from '@mui/material';
import { ACTIVE_PRODUCT, INTEGRALDELTA } from '../config/productTarget';

/** The two questions a click can ask. Exported so the caller names them rather than passing 'FROM'. */
export const SPIRAL_CLICK_MODES = { FROM: 'FROM', TO: 'TO' };

const MODE_HELP = {
  FROM: 'Click a bar to project its ladder FORWARD: where this turn points, and what it hit.',
  TO: 'Click a bar to trace its lineage BACKWARD: which earlier turns point at it.',
};

/**
 * Turns plain left-click on a bar into a spiral inspection, and clears what it draws.
 *
 * <p>The spiral study was reachable only through its dialog: open it, pick a mode, pick a pivot,
 * run. That is the right shape for configuring a scan and the wrong one for exploring — reading a
 * chart means asking the same question of one bar after another, and a modal between every question
 * makes that a chore rather than a look around.
 *
 * <p><b>Two modes, stated rather than inferred.</b> FROM projects a bar's ladder forward; TO traces
 * which earlier turns point at it. The first version chose between them by where the bar sat — a
 * historical bar got the lineage, a recent one got the projection — which is a reasonable default
 * and a bad rule: both questions are legitimate about ANY bar, and a user who wants to know what a
 * turn from 2015 predicted could not ask.
 *
 * <p><b>Off by default.</b> Left-click already drives the drawing tools and the two-bar selection,
 * so enabling this silently would change what an ordinary click does with nothing on screen saying
 * why. The chip is the thing that says why, which is the reason it is a visible control rather than
 * a hidden modifier key.
 *
 * <p><b>Clear appears only when there is something to clear.</b> A permanently visible Clear button
 * next to a study that is not drawn invites a click that does nothing, and a control that sometimes
 * does nothing is indistinguishable from one that is broken.
 */
export default function SpiralClickControl({
  mode, onSelectMode, canClear, onClear, shiftDown, product = ACTIVE_PRODUCT,
}) {
  /*
   * ⚠ NOT RENDERED AT ALL ON integralDelta, and the WHOLE control goes rather than the two chips.
   *
   * The brief asked for `⚡ Φ FROM` and `⚡ Φ TO` to be unrendered. Hiding only those two would
   * leave this component's absolutely-positioned Box on the chart holding nothing: `Clear` is the
   * only other child and it appears solely when arcs are drawn, which cannot happen on a product
   * where the scan is denied. An empty positioned box over the canvas is invisible until it is
   * not — it still takes a layer at `zIndex: 5` and still offsets the scan badge below it.
   *
   * <p>And the whole surface is Φ: both chips, the Clear, and the click-mode they arm. There is no
   * part of it that belongs to a product without the spiral study.
   *
   * <p>⚠ THE ROOT CAUSE IS THAT THIS WAS NEVER GATED. `spiral_reversal_scan` has been in
   * `PRODUCT_DENIES.integraldelta` since the deny lists landed, so the TOOLBAR icon was hidden and
   * this control went on rendering beside the chart — the deny list governs the icon that LAUNCHES
   * a study, not every surface the study owns. A control that cannot be launched from the toolbar
   * is still on screen if nothing hides it, which is the same shape as the header brand and the
   * two indicator tooltips: the gate was product-aware and a sibling was not.
   */
  if (product === INTEGRALDELTA) return null;
  const chipFor = (value, label) => {
    const active = mode === value;
    return (
      <Tooltip arrow placement="bottom-start" title={MODE_HELP[value]} key={value}>
        <Chip
          label={label}
          // Selecting the active mode turns it off. A pair of modes with no way back to neither
          // would leave left-click permanently captured from the drawing tools.
          onClick={() => onSelectMode(active ? null : value)}
          size="small"
          aria-pressed={active}
          sx={{
            fontWeight: 700,
            cursor: 'pointer',
            color: active ? '#0d3b16' : '#37474f',
            backgroundColor: active ? 'rgba(129, 199, 132, 0.95)' : 'rgba(207, 216, 220, 0.92)',
            border: active
              ? '1px solid rgba(46, 125, 50, 0.9)'
              : '1px solid rgba(120, 144, 156, 0.8)',
            '&:hover': {
              backgroundColor: active ? 'rgba(102, 187, 106, 1)' : 'rgba(176, 190, 197, 0.98)',
            },
          }}
        />
      </Tooltip>
    );
  };

  return (
    <Box
      sx={{
        position: 'absolute',
        // Drops below the scan badge when that is on screen, so the two never overlap. The badge
        // renders nothing when no zone was found, and in that case this takes the top slot.
        top: shiftDown ? 42 : 8,
        left: 12,
        // Same layer as the badge: without it the chart canvas paints over both.
        zIndex: 5,
        display: 'flex',
        gap: 0.75,
        alignItems: 'center',
      }}
    >
      {chipFor(SPIRAL_CLICK_MODES.FROM, '⚡ Φ FROM')}
      {chipFor(SPIRAL_CLICK_MODES.TO, '⚡ Φ TO')}

      {canClear && (
        <Tooltip arrow placement="bottom-start" title="Remove the spiral arcs and markers from the chart">
          <Chip
            label="Clear"
            onClick={onClear}
            size="small"
            sx={{
              fontWeight: 600,
              cursor: 'pointer',
              color: '#37474f',
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              border: '1px solid rgba(120, 144, 156, 0.8)',
              '&:hover': { backgroundColor: 'rgba(236, 239, 241, 1)' },
            }}
          />
        </Tooltip>
      )}
    </Box>
  );
}

SpiralClickControl.propTypes = {
  /** The build target. Defaults to the baked one, so a caller that omits it still gates. */
  product: PropTypes.string,
  /** 'FROM', 'TO', or null when a click belongs to the drawing tools again. */
  mode: PropTypes.oneOf(['FROM', 'TO', null]),
  onSelectMode: PropTypes.func.isRequired,
  /** Whether an inspection is currently drawn. Clear is hidden when it is not. */
  canClear: PropTypes.bool,
  onClear: PropTypes.func.isRequired,
  /** True when the scan badge occupies the top-left slot, so this sits under it. */
  shiftDown: PropTypes.bool,
};
